import { css, type Theme } from '@emotion/react';
import { ArrowCircleUp, PlusCircle, XCircle, WarningCircle } from '@phosphor-icons/react';
import type { StatusTreeComponentPendingChanges } from 'common/status/StatusTree';
import { ListItemText } from 'client/components/Other/ListItemText';
import { Tag, type TagProps } from 'client/components/Tag';

type ComponentState = StatusTreeComponentPendingChanges['state'];

const stateToIcon: Record<ComponentState, JSX.Element> = {
  new: <PlusCircle weight="bold" size={24} />,
  modified: <ArrowCircleUp weight="bold" size={24} />,
  removed: <XCircle weight="bold" size={24} />,
  error: <WarningCircle weight="bold" size={24} />,
};

const stateToText: Record<ComponentState, string> = {
  new: 'Added',
  modified: 'Modified',
  removed: 'Removed',
  error: 'Error',
};

const stateToTagType: Record<ComponentState, TagProps['type']> = {
  new: 'success',
  modified: 'primary',
  removed: 'error',
  error: 'error',
};

export const PendingChangesComponents = ({ components }: { components: StatusTreeComponentPendingChanges[] }) => {
  return (
    <ul css={listStyle}>
      {components.map((component, index) => (
        <Tag
          key={`${component.stackComponentID}-${index}`}
          component="li"
          type={stateToTagType[component.state]}
          className={stateToTagType[component.state]}
          css={tagStyle}
        >
          {stateToIcon[component.state]}
          <ListItemText primary={component.identifier} secondary={stateToText[component.state]} />
        </Tag>
      ))}
    </ul>
  );
};

const listStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0%;
  list-style: none;
`;

const tagStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 10px;

  &.success > svg {
    color: ${theme.color.green[100]};
  }

  &.primary > svg {
    color: ${theme.color.primary[100]};
  }

  &.error > svg {
    color: ${theme.color.red[100]};
  }

  .body1 {
    margin-bottom: -4px;
  }
`;
